export const REVIEW_ROLES = [
  "current_state",
  "expected_change",
  "value_math",
  "costs_policy",
] as const;

export type ReviewRole = (typeof REVIEW_ROLES)[number];

export const REVIEW_ROLE_LABELS: Record<ReviewRole, string> = {
  current_state: "Current State",
  expected_change: "Expected Change",
  value_math: "Value Math",
  costs_policy: "Costs & Policy",
};

export interface ReviewableValueStream {
  id: string;
  name: string;
  direction: "benefit" | "cost";
  formulaExpr: string;
  formulaDescription?: string;
  annualValue?: number;
}

export const SOURCE_TYPES = [
  "citation",
  "benchmark",
  "model_explanation",
  "assumption_source",
  "profile_snapshot",
  "evidence_grounded",
] as const;

export type ReviewableSourceType = (typeof SOURCE_TYPES)[number];

export interface ReviewableSourceRef {
  id: string;
  label: string;
  sourceType: ReviewableSourceType;
  url?: string;
}

export const PROVENANCE_SOURCES = [
  "user_provided",
  "derived",
  "benchmark",
  "archetype_default",
] as const;

export type ProvenanceSource = (typeof PROVENANCE_SOURCES)[number];

export interface InputProvenance {
  source: ProvenanceSource;
  derivation?: string;
  confidence?: "high" | "moderate" | "low";
  snapshotAt: string;
  profileUpdatedAt?: string;
}

export interface ReviewableInput {
  id: string;
  label: string;
  inputType: "assumption" | "context";
  currentValue: number | string;
  unit?: string;
  format?: string;
  description?: string;
  derivation?: string;
  range?: {
    low: number;
    high: number;
    step?: number;
  };
  linkedValueStreams: string[];
  formulaRefs: string[];
  sourceRefs: string[];
  orgAnchorRefs: string[];
  provenance?: InputProvenance;
}

export interface AssumptionMateriality {
  inputId: string;
  normalizedScore: number;
  source: "assumption_sensitivity";
}

export interface ReviewableModel {
  modelKind: "category_modeled" | "custom";
  modelId: string;
  generatedAt: string;
  valueStreams: ReviewableValueStream[];
  inputs: ReviewableInput[];
  sources: ReviewableSourceRef[];
  assumptionMateriality: AssumptionMateriality[];
}

export const BASED_ON_VALUES = [
  "local_data",
  "profile_derived",
  "independent_research",
  "peer_benchmark",
  "vendor_claim",
  "quote",
  "market_range",
  "model_policy",
] as const;

export type BasedOn = (typeof BASED_ON_VALUES)[number];

export const FIT_FOR_ORG_VALUES = ["high", "moderate", "low"] as const;
export type FitForOrg = (typeof FIT_FOR_ORG_VALUES)[number];

export const SUBROLES = [
  "baseline",
  "derived_context",
  "deployment",
  "efficiency",
  "outcome",
  "economics",
  "commercial",
  "guardrail",
] as const;

export type Subrole = (typeof SUBROLES)[number];

export interface ModelReviewAnnotation {
  inputId: string;
  reviewRole: ReviewRole;
  subrole?: Subrole;
  basedOn: BasedOn;
  fitForOrg: FitForOrg;
  supportingSourceRefs: string[];
  supportingOrgAnchorRefs: string[];
  whyReasonable: string;
  whatToConfirm: string;
  evidenceLimits: string;
}

export interface LaneSummary {
  reviewRole: ReviewRole;
  summary: string;
  weakestInputIds: string[];
  whatWouldIncreaseTrust: string;
}

export interface ModelReviewMap {
  headline: string;
  laneSummaries: LaneSummary[];
  reviewFirstInputIds: string[];
}

export const MODEL_REVIEW_STATES = [
  "fresh",
  "stale_profile",
  "outdated_model",
] as const;

export type ModelReviewState = (typeof MODEL_REVIEW_STATES)[number];

/* ---------- key risks ---------- */

/**
 * The rule that produced a risk. Ordered here from the systemic ("nothing in
 * this model is settled yet") to the framing ("what kind of benefit this is"),
 * which is also the tiebreak order when two risks score the same materiality.
 *
 * `scale_mismatch` is reserved rather than emitted: deciding that the cited
 * evidence covers a smaller footprint than the deployment needs the scope
 * facts, which are not on the read model yet.
 */
export const RISK_CATEGORIES = [
  "needs_input",
  "modeling_gap",
  "scale_mismatch",
  "weakest_input",
  "source_trust",
  "assumed_horizon",
  "benefit_type",
] as const;

export type RiskCategory = (typeof RISK_CATEGORIES)[number];

/**
 * One entry in the deterministic key-risks register.
 *
 * The register is ordered most → least material by the *server*, and that
 * order is the content — a client renders it as given and never sorts.
 */
export interface ModelReviewRisk {
  /** Stable within one artifact; a render key and a test handle, not a ref. */
  id: string;
  category: RiskCategory;
  /** Reader-facing prose. One sentence, naming this model's own weakness. */
  text: string;
  /** 0–1, higher is more material. Decides the register's order. */
  materiality: number;
  /**
   * The evidence this risk is about, as a **source identifier** —
   * a `FactSource.id` or an `EvidenceReference.dedupKey`.
   *
   * **Never a citation number.** Bibliography ordinals are assigned when the
   * report is mapped and are not durable across model regenerations, so a
   * persisted artifact that stored one would go silently wrong the next time
   * the bibliography changed shape. The mapper resolves identifier → number.
   */
  sourceId: string | null;
}

export interface ModelReviewArtifact {
  version: "v1";
  generatedAt: string;
  state: ModelReviewState;
  modelInputHash: string;
  profileSnapshotHash: string;
  /**
   * Deterministic key-risks register, ordered most → least material.
   * Empty when no rule fires — a model with nothing to caveat is a real
   * outcome, and the report renders its empty state.
   */
  risks: ModelReviewRisk[];
  /**
   * The LLM annotation pass (`LLMAnnotationOutput`) has never run: the
   * deterministic risk builder writes `risks` and nothing else. These four
   * are optional so a rules-only artifact is a valid artifact rather than one
   * padded out with empty objects that read as "the model reviewed this and
   * found nothing".
   */
  annotationModel?: string;
  reviewableModel?: ReviewableModel;
  annotations?: ModelReviewAnnotation[];
  reviewMap?: ModelReviewMap;
}

export interface ReviewRankingConfig {
  basedOnWeights: Record<BasedOn, number>;
  fitForOrgWeights: Record<FitForOrg, number>;
  unconfirmedWeight: number;
}

export const DEFAULT_REVIEW_RANKING_CONFIG: ReviewRankingConfig = {
  basedOnWeights: {
    local_data: 0,
    profile_derived: 0.05,
    independent_research: 0.1,
    peer_benchmark: 0.2,
    market_range: 0.3,
    quote: 0.1,
    vendor_claim: 0.4,
    model_policy: 0.1,
  },
  fitForOrgWeights: {
    high: 0,
    moderate: 0.2,
    low: 0.4,
  },
  unconfirmedWeight: 0.1,
};

/**
 * The portion of review weakness that can be derived without an LLM
 * annotation. Callers supply whether the evidence is still unconfirmed;
 * annotations and the assumptions read model have different fields for that
 * fact, but must use the same weights.
 */
export function computeBasedOnEvidenceWeakness(
  basedOn: BasedOn | null | undefined,
  hasUnconfirmedEvidence: boolean,
  config: ReviewRankingConfig = DEFAULT_REVIEW_RANKING_CONFIG,
): number {
  let score = basedOn ? (config.basedOnWeights[basedOn] ?? 0) : 0;
  if (hasUnconfirmedEvidence) score += config.unconfirmedWeight;
  return Math.min(1, Math.max(0, score));
}

export function computeWeakness(
  annotation: ModelReviewAnnotation,
  config: ReviewRankingConfig = DEFAULT_REVIEW_RANKING_CONFIG,
): number {
  let score = computeBasedOnEvidenceWeakness(
    annotation.basedOn,
    annotation.whatToConfirm.trim().length > 0,
    config,
  );
  score += config.fitForOrgWeights[annotation.fitForOrg] ?? 0;
  return Math.min(1, Math.max(0, score));
}

export interface LLMAnnotationOutput {
  annotations: ModelReviewAnnotation[];
  headline: string;
  laneSummaries: LaneSummary[];
}
